import { AxiosInstance, AxiosResponse } from "axios";
import i18next from "i18next";
import { Result } from "services/Result";
import { ShopCertificationTestsResponse } from "./types";

type TokenHandlerResponse =
  | {
    status: 'tokenReturned';
    token: string;
    isSubscriptionPurchased: false;
    errorMessage?: undefined;
  }
  | {
    status: 'success';
    token?: undefined;
    isSubscriptionPurchased: true;
    errorMessage?: undefined;
  }
  | {
    status: 'error';
    token?: undefined;
    isSubscriptionPurchased: false;
    errorMessage: {
      title: string;
      status: number;
      detail: string;
      Exception: string;
    };
  };

export class TestOneTimePaymentService {
  constructor(private readonly axiosInstance: AxiosInstance) { }

  async tokenHandler(
    tokenId: string,
    email: string,
    subscriptionPlanCode: string | null,
    currency: string,
    couponCode?: string,
    includeSubscription?: boolean,
    productIds?: string[]
  ): Promise<TokenHandlerResponse> {
    const response = await this.axiosInstance.post(`/api/PaymentAdmin`, {
      TokenId: tokenId,
      Email: email,
      SubscriptionPlanCode: subscriptionPlanCode,
      Currency: currency,
      CouponCode: couponCode,
      IncludeSubscription: includeSubscription ?? true,
      ProductIds: productIds ?? [],
    });

    if (response.data) {
      if (response.status === 406) {
        //if 406 is returned it means that 3D secure handling is required and we need to save params
        return {
          status: 'tokenReturned',
          token: response.data,
          isSubscriptionPurchased: false,
        }; //threeDSecureActionTokenId is needed to start 3D Secure challenge
      } else if (!response.data.isError && response.data.data === true) {
        return { status: 'success', isSubscriptionPurchased: true };
      } else {
        return {
          status: 'error',
          isSubscriptionPurchased: false,
          errorMessage: response.data.detail,
        };
      }
    }

    throw Error(i18next.t('3DSecureTokenError', { ns: 'organizationPage' }));
  }

  async sendThreeDSecureRequest(
    tokenId: string,
    email: string,
    subscriptionPlanCode: string | null,
    currency: string,
    couponCode: string,
    threeDSecureTokenId: string,
    includeSubscription?: boolean,
    productIds?: string[]
  ): Promise<AxiosResponse<any, any>> {
    const response = await this.axiosInstance.post(`/api/PaymentAdmin`, {
      TokenId: tokenId,
      Email: email,
      SubscriptionPlanCode: subscriptionPlanCode,
      Currency: currency,
      CouponCode: couponCode,
      ThreeDSecureToken: threeDSecureTokenId,
      IncludeSubscription: includeSubscription ?? true,
      ProductIds: productIds ?? [],
    });

    if (response.data) return response;

    throw Error(i18next.t('3DSecureRequestError', { ns: 'organizationPage' }));
  }

  async getShopCertificationTests(): Promise<ShopCertificationTestsResponse> {
    const response = await this.axiosInstance.get<Result<ShopCertificationTestsResponse>>(`api/shop-certification-tests`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while fetching shop certification tests.');
  }
}